import { toast as sonnerToast } from 'sonner';

/**
 * Toast notification utilities
 * Wrapper around sonner with consistent styling and behavior
 */

interface ToastOptions {
  id?: string | number;
  description?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Show a success toast
 * @example toast.success('Draft generated')
 * @example toast.success('Draft generated', { description: 'Ready to insert into Front' })
 */
export const success = (message: string, options?: ToastOptions) => {
  return sonnerToast.success(message, {
    id: options?.id,
    duration: options?.duration ?? 5000,
    description: options?.description,
    action: options?.action,
  });
};

/**
 * Show an error toast
 * @example toast.error('Failed to generate draft')
 * @example toast.error('API Error', { description: 'Please try again' })
 */
export const error = (message: string, options?: ToastOptions) => {
  return sonnerToast.error(message, {
    id: options?.id,
    duration: options?.duration ?? 7000, // Errors stay longer
    description: options?.description,
    action: options?.action,
  });
};

/**
 * Show an info toast
 * @example toast.info('Analyzing conversation...')
 */
export const info = (message: string, options?: ToastOptions) => {
  return sonnerToast.info(message, {
    id: options?.id,
    duration: options?.duration ?? 5000,
    description: options?.description,
    action: options?.action,
  });
};

/**
 * Show a warning toast
 * @example toast.warning('Low confidence draft')
 * @example toast.warning('Low confidence', { description: 'Please review carefully' })
 */
export const warning = (message: string, options?: ToastOptions) => {
  return sonnerToast.warning(message, {
    id: options?.id,
    duration: options?.duration ?? 6000,
    description: options?.description,
    action: options?.action,
  });
};

/**
 * Show a loading toast (useful for async operations)
 * Returns a toast ID that can be used to dismiss or update
 * @example const id = toast.loading('Generating draft...')
 * @example toast.success('Draft generated', { id }) // Updates the loading toast
 */
export const loading = (message: string, options?: Omit<ToastOptions, 'action'>) => {
  return sonnerToast.loading(message, {
    id: options?.id,
    duration: Infinity, // Loading toasts don't auto-dismiss
    description: options?.description,
  });
};

/**
 * Show a promise toast (automatically handles loading/success/error states)
 * @example
 * toast.promise(
 *   generateDraft(),
 *   {
 *     loading: 'Generating draft...',
 *     success: 'Draft generated',
 *     error: 'Failed to generate draft'
 *   }
 * )
 */
export const promise = <T,>(
  promiseOrFn: Promise<T> | (() => Promise<T>),
  options: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: Error) => string);
    description?: string;
  }
) => {
  return sonnerToast.promise(promiseOrFn, {
    loading: options.loading,
    success: options.success,
    error: options.error,
    description: options.description,
  });
};

/**
 * Dismiss a specific toast by ID or all toasts
 * @example toast.dismiss() // Dismiss all
 * @example toast.dismiss(toastId) // Dismiss specific toast
 */
export const dismiss = (toastId?: string | number) => {
  return sonnerToast.dismiss(toastId);
};

/**
 * Show a custom toast (with full control)
 * @example toast.custom('Custom message', { duration: 10000 })
 */
export const custom = (message: string, options?: ToastOptions) => {
  return sonnerToast(message, {
    id: options?.id,
    duration: options?.duration ?? 5000,
    description: options?.description,
    action: options?.action,
  });
};

/**
 * Default export with all methods
 */
export const toast = {
  success,
  error,
  info,
  warning,
  loading,
  promise,
  dismiss,
  custom,
};

export default toast;
